import { useState } from 'react';

import {
  useFloating,
  useInteractions,
  shift,
  offset,
  flip,
  useClick,
  useDismiss,
  OffsetOptions,
  autoUpdate,
  Placement,
} from '@floating-ui/react';

interface UsePopoutOptions {
  placement?: Placement;
  offset?: OffsetOptions;
  onClose?: () => void;
}

export function usePopout(options?: UsePopoutOptions) {
  const [isOpen, setIsOpen] = useState(false);

  function onOpenChange(open: boolean) {
    setIsOpen(open);
    if (!open) {
      options?.onClose?.();
    }
  }

  const { refs, x, y, strategy, context } = useFloating({
    open: isOpen,
    onOpenChange,
    placement: options?.placement || 'bottom-start',
    middleware: [shift(), flip(), offset(options?.offset || 10)],
    whileElementsMounted: autoUpdate,
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, click]);

  const floatingStyles: React.CSSProperties = {
    position: strategy,
    top: y ?? 0,
    left: x ?? 0,
  };

  return {
    isOpen,
    setIsOpen,
    getReferenceProps,
    getFloatingProps,
    floatingStyles,
    refs,
  };
}

export { FloatingPortal } from '@floating-ui/react';
